import { forwardRef } from 'react';
import { CartApprovalStatus } from 'src/types';

type Props = {
  answer: 'notRequired' | 'required' | 'unknown';
  setAnswer: (answer: 'notRequired' | 'required') => void;
  handleClickPrimaryAction: () => void;
  shouldShowNamedApprovers: boolean;
  isInitialLayer: boolean;
  currentStatus: CartApprovalStatus | undefined;
};

/**
 * Configuration object for layer-specific text content
 */
const TEXT_CONFIG = {
  initial: {
    title: 'Approval Required?',
    description:
      'Does this cart need to be approved by your organization? (e.g if the organization needs to sign off on purchases, etc)',
    buttonOneText: 'No Approval Required',
    buttonTwoText: 'Requires Approval',
    finalButtonText: {
      default: 'Select an Option',
      required: {
        namedApproversEnabled: 'Select Approver',
        namedApproversDisabled: 'Send for Approval',
      },
      notRequired: 'Get Cart Now!',
    },
  },
  subsequent: {
    title: 'Additional Approval Required?',
    description:
      'Does this cart need to be approved by someone else in your organization?',
    buttonOneText: 'No Approval Required',
    buttonTwoText: 'Requires Approval',
    finalButtonText: {
      default: 'Select an Option',
      required: {
        namedApproversEnabled: 'Select Next Approver',
        namedApproversDisabled: 'Send for Next Approval',
      },
      notRequired: 'Approve Cart!',
    },
  },
  rejected: {
    title: 'Resubmit Cart?',
    description:
      'Would you like to resubmit the cart for approval or simply load the cart for you to purchase yourself?',
    buttonOneText: 'Load Cart',
    buttonTwoText: 'Resubmit Cart',
    finalButtonText: {
      default: 'Select an Option',
      required: {
        namedApproversEnabled: 'Select Next Approver',
        namedApproversDisabled: 'Send for Next Approval',
      },
      notRequired: 'Load Cart',
    },
  },
} as const;

export const InitialView = forwardRef<
  HTMLDivElement | HTMLButtonElement,
  Props
>(
  (
    {
      answer,
      setAnswer,
      handleClickPrimaryAction,
      shouldShowNamedApprovers,
      isInitialLayer,
      currentStatus,
    },
    ref,
  ) => {
    const textConfig = isInitialLayer
      ? TEXT_CONFIG.initial
      : currentStatus === 'rejected'
        ? TEXT_CONFIG.rejected
        : TEXT_CONFIG.subsequent;

    const getFinalButtonText = () => {
      if (answer === 'unknown') {
        return textConfig.finalButtonText.default;
      }
      if (answer === 'required') {
        return shouldShowNamedApprovers
          ? textConfig.finalButtonText.required.namedApproversEnabled
          : textConfig.finalButtonText.required.namedApproversDisabled;
      }
      return textConfig.finalButtonText.notRequired;
    };

    return (
      <>
        <h4 className='heading-300 text-primary-dark'>{textConfig.title}</h4>
        <p className='text-300 text-pretty text-center text-primary-dark'>
          {textConfig.description}
        </p>

        <div
          className='flex w-full flex-col items-center justify-center gap-0 sm:flex-row sm:flex-nowrap'
          ref={
            answer === 'unknown'
              ? (ref as React.Ref<HTMLDivElement>)
              : undefined
          }
        >
          <button
            onClick={() => setAnswer('notRequired')}
            className={`button-secondary h-12 w-full rounded-b-none text-center sm:w-auto sm:rounded-r-none sm:rounded-bl-full ${answer === 'notRequired' ? 'bg-blueGray hover:bg-blueGray/80' : 'bg-offWhite'}`}
          >
            {textConfig.buttonOneText}
          </button>
          <button
            onClick={() => setAnswer('required')}
            className={`button-secondary h-12 w-full rounded-t-none text-center sm:w-auto sm:rounded-l-none sm:rounded-tr-full ${answer === 'required' ? 'bg-blueGray hover:bg-blueGray/80' : 'bg-offWhite'}`}
          >
            {textConfig.buttonTwoText}
          </button>
        </div>

        <button
          disabled={answer === 'unknown'}
          className='button-primary h-12'
          onClick={handleClickPrimaryAction}
          ref={
            answer !== 'unknown'
              ? (ref as React.Ref<HTMLButtonElement>)
              : undefined
          }
        >
          {getFinalButtonText()}
        </button>
      </>
    );
  },
);
